/** Pravý panel editoru: breadcrumb hierarchie (skok na rodiče) + vlastnosti vybraného uzlu
 *  (kontejner / mřížka-tabulka / widget) + per-strana margin/padding + size + paleta (drag i klik). */

import { useDraggable } from "@dnd-kit/core";
import { ArrowDown, ArrowUp, Copy, LayoutGrid, Plus, SquareStack, Table, Trash2 } from "lucide-react";
import React from "react";
import { findNode, findPath, getWidgetType, type LayoutNode, type Sides, type WidgetTypeEntry, widgetTypesByCategory } from "@globals/layouts";
import { FieldRenderer } from "@client/components/layout/FieldRenderer";
import { cn } from "@client/lib/cn";
import { useLayoutEditor } from "@client/store/layoutEditor";

const sel = "w-full rounded border border-input bg-background px-2 py-1 text-xs";
const optBtn = "inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-[11px] hover:bg-accent";
const sideKeys = ["t", "r", "b", "l"] as const;

const nodeLabel = (n: LayoutNode): string => {
  if (n.kind === "widget") return getWidgetType(n.type)?.label ?? n.type;
  if (n.kind === "grid") return "Tabulka";
  if (n.kind === "tabs") return "Záložky";
  return n.dir === "row" ? "Řádek" : "Sloupec";
};

/** 4 mini-inputy (T R B L) pro margin/padding. number → rozloží na všechny strany. */
const SidesEditor: React.FC<{ label: string; value: number | Sides | undefined; onChange: (v: Sides) => void }> = ({ label, value, onChange }) => {
  const s: Sides = typeof value === "number" ? { t: value, r: value, b: value, l: value } : value ?? {};
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="grid grid-cols-4 gap-1">
        {sideKeys.map((k) => (
          <input
            key={k}
            type="number"
            min={0}
            className={sel + " px-1 text-center"}
            placeholder={k.toUpperCase()}
            title={{ t: "nahoře", r: "vpravo", b: "dole", l: "vlevo" }[k]}
            value={s[k] ?? ""}
            onChange={(e) => onChange({ ...s, [k]: e.target.value === "" ? undefined : Number(e.target.value) || 0 })}
          />
        ))}
      </div>
    </div>
  );
};

/** Položka palety — klik přidá do cíle, drag pustí do libovolného kontejneru/tabulky (data `new`). */
const PaletteItem: React.FC<{ w: WidgetTypeEntry; onAdd: () => void }> = ({ w, onAdd }) => {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: "new:" + w.id,
    data: { kind: "new", widgetType: w.id, cfg: w.defaultConfig ?? {}, size: w.defaultSize, label: w.label },
  });
  return (
    <button ref={setNodeRef} {...listeners} {...attributes} type="button" className={optBtn + (isDragging ? " opacity-50" : "")} title={w.description} onClick={onAdd}>
      {w.label}
    </button>
  );
};

const ContainerProps: React.FC<{ node: Extract<LayoutNode, { kind: "container" }> }> = ({ node }) => {
  const patch = useLayoutEditor((s) => s.patchNode);
  const set = (p: Partial<typeof node>) => patch(node.id, p as Partial<LayoutNode>);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        <button className={optBtn + (node.dir === "row" ? " bg-primary text-primary-foreground" : "")} onClick={() => set({ dir: "row" })}>Řádek →</button>
        <button className={optBtn + (node.dir === "col" ? " bg-primary text-primary-foreground" : "")} onClick={() => set({ dir: "col" })}>Sloupec ↓</button>
      </div>
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Zarovnání obsahu (justify)</span>
        <select className={sel} value={node.justify} onChange={(e) => set({ justify: e.target.value as typeof node.justify })}>
          {["start", "center", "end", "between", "around", "evenly"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Zarovnání napříč (align)</span>
        <select className={sel} value={node.align} onChange={(e) => set({ align: e.target.value as typeof node.align })}>
          {["start", "center", "end", "stretch", "baseline"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Mezera mezi prvky (gap)</span>
        <input type="number" className={sel} min={0} max={64} value={node.gap} onChange={(e) => set({ gap: Number(e.target.value) || 0 })} /></label>
      <SidesEditor label="Padding (vnitřní)" value={node.padding} onChange={(v) => set({ padding: v })} />
      <label className="flex items-center gap-2"><input type="checkbox" checked={node.wrap} onChange={(e) => set({ wrap: e.target.checked })} /><span className="text-xs">Zalamovat (responzivní)</span></label>
      <label className="flex items-center gap-2"><input type="checkbox" checked={node.stackOnMobile ?? false} onChange={(e) => set({ stackOnMobile: e.target.checked })} /><span className="text-xs">Skládat na mobilu</span></label>
    </div>
  );
};

const GridProps: React.FC<{ node: Extract<LayoutNode, { kind: "grid" }> }> = ({ node }) => {
  const patch = useLayoutEditor((s) => s.patchNode);
  const set = (p: Partial<typeof node>) => patch(node.id, p as Partial<LayoutNode>);
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Počet sloupců {node.minColW ? "(jen strop, viz responzivní)" : ""}</span>
        <input type="number" className={sel} min={1} max={24} value={node.cols} onChange={(e) => set({ cols: Math.max(1, Number(e.target.value) || 1) })} /></label>
      <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <input type="checkbox" checked={node.minColW != null} onChange={(e) => set({ minColW: e.target.checked ? 240 : undefined } as Partial<typeof node>)} />
        Responzivní (přetékání podle šířky)
      </label>
      {node.minColW != null && (
        <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Min. šířka buňky (px)</span>
          <input type="number" className={sel} min={40} max={1000} value={node.minColW} onChange={(e) => set({ minColW: Math.max(40, Number(e.target.value) || 40) })} /></label>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Mezera řádky</span>
          <input type="number" className={sel} min={0} max={64} value={node.rowGap} onChange={(e) => set({ rowGap: Number(e.target.value) || 0 })} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Mezera sloupce</span>
          <input type="number" className={sel} min={0} max={64} value={node.colGap} onChange={(e) => set({ colGap: Number(e.target.value) || 0 })} /></label>
      </div>
    </div>
  );
};

const SizeProps: React.FC<{ node: LayoutNode }> = ({ node }) => {
  const setSize = useLayoutEditor((s) => s.setSize);
  const sz = node.size ?? {};
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-3 gap-2">
        <label className="flex flex-col gap-0.5"><span className="text-[10px] text-muted-foreground">grow</span>
          <input type="number" className={sel} value={sz.grow ?? 0} onChange={(e) => setSize(node.id, { ...sz, grow: Number(e.target.value) || 0 })} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] text-muted-foreground">basis</span>
          <input className={sel} value={sz.basis ?? ""} placeholder="auto/100%/240px" onChange={(e) => setSize(node.id, { ...sz, basis: e.target.value || undefined })} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] text-muted-foreground">výška px</span>
          <input type="number" className={sel} value={typeof sz.height === "number" ? sz.height : ""} onChange={(e) => setSize(node.id, { ...sz, height: e.target.value === "" ? undefined : Number(e.target.value) })} /></label>
      </div>
      <SidesEditor label="Margin (vnější)" value={sz.margin} onChange={(v) => setSize(node.id, { ...sz, margin: v })} />
    </div>
  );
};

export const EditorPanel: React.FC<{ scope: "dashboard" | "device"; device?: "pip" | "tracer" | "bms"; slug?: string }> = ({ scope, device, slug }) => {
  const doc = useLayoutEditor((s) => s.doc);
  const selectedId = useLayoutEditor((s) => s.selectedId);
  const { select, addWidget, addContainer, addGrid, remove, duplicate, wrap, reorder, setConfig, setGridChild } = useLayoutEditor();

  if (!doc) return null;
  const found = selectedId ? findNode(doc.root, selectedId) : null;
  const node = found?.node ?? doc.root;
  const canContain = node.kind === "container" || node.kind === "grid";
  const targetId = canContain ? node.id : doc.root.id;
  const widgetType = node.kind === "widget" ? getWidgetType(node.type) : undefined;
  const path = findPath(doc.root, node.id) ?? [node.id];
  const gridChild = found?.parent?.kind === "grid" ? found.parent.children.find((c) => c.node.id === node.id) : undefined;

  return (
    <div className="sticky top-14 flex max-h-[calc(100vh-4rem)] w-72 shrink-0 flex-col gap-3 self-start overflow-y-auto rounded-lg border border-border bg-card p-3 text-sm">
      {/* Hierarchie — klik = skok na rodiče */}
      <div className="flex flex-wrap items-center gap-0.5 text-[11px]">
        {path.map((id, i) => {
          const n = findNode(doc.root, id)?.node;
          if (!n) return null;
          const last = i === path.length - 1;
          return (
            <React.Fragment key={id}>
              {i > 0 && <span className="text-muted-foreground">›</span>}
              <button className={cn("rounded px-1 hover:bg-accent", last ? "font-semibold text-foreground" : "text-muted-foreground")} onClick={() => select(id)}>
                {nodeLabel(n)}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Akce na vybranou komponentu */}
      {found && (
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">Tato komponenta</div>
          <div className="flex flex-wrap gap-1">
            <button className={optBtn} title="Posunout nahoru / dříve" onClick={() => reorder(node.id, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button className={optBtn} title="Posunout dolů / později" onClick={() => reorder(node.id, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
            <button className={optBtn} title="Obalit do nového řádku" onClick={() => wrap(node.id, "row")}><SquareStack className="h-3.5 w-3.5" />řádek</button>
            <button className={optBtn} title="Obalit do nového sloupce" onClick={() => wrap(node.id, "col")}><SquareStack className="h-3.5 w-3.5" />sloupec</button>
            <button className={optBtn} title="Duplikovat" onClick={() => duplicate(node.id)}><Copy className="h-3.5 w-3.5" /></button>
            <button className={cn(optBtn, "border-destructive/40 text-destructive hover:bg-destructive/10")} title="Smazat" onClick={() => remove(node.id)}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}

      {/* colSpan, když je uzel v tabulce */}
      {gridChild && found?.parent?.kind === "grid" && (
        <label className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Šířka v tabulce (sloupců)</span>
          <input type="number" min={1} max={found.parent.cols} className={sel} value={gridChild.colSpan} onChange={(e) => setGridChild(node.id, { colSpan: Math.max(1, Number(e.target.value) || 1) })} /></label>
      )}

      {node.kind === "container" && <ContainerProps node={node} />}
      {node.kind === "grid" && <GridProps node={node} />}

      {node.kind === "widget" && widgetType && (
        <div className="flex flex-col gap-2">
          {widgetType.configFields.map((f) => (
            <FieldRenderer key={f.key} spec={f} value={node.config[f.key]} onChange={(v) => setConfig(node.id, f.key, v)} deviceType={scope === "device" ? device : undefined} slug={slug} />
          ))}
        </div>
      )}

      <div className="border-t border-border pt-2">
        <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">Velikost & okraje</div>
        <SizeProps node={node} />
      </div>

      {/* Přidat DOVNITŘ cílové komponenty */}
      <div className="border-t border-border pt-2">
        <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
          {canContain ? <>Přidat DOVNITŘ: <span className="text-foreground">{nodeLabel(node)}</span></> : "Přidat do kořene"}
        </div>
        <div className="mb-2 flex flex-wrap gap-1">
          <button className={optBtn} title="Nový řádek (prvky vedle sebe)" onClick={() => addContainer(targetId, "row")}><Plus className="h-3 w-3" />Řádek</button>
          <button className={optBtn} title="Nový sloupec (prvky pod sebou)" onClick={() => addContainer(targetId, "col")}><Plus className="h-3 w-3" />Sloupec</button>
          <button className={optBtn} title="Statická tabulka (mřížka N sloupců)" onClick={() => addGrid(targetId, 2)}><Table className="h-3 w-3" />Tabulka</button>
          <button className={optBtn} title="Responzivní tabulka (zalamuje dle šířky)" onClick={() => addContainer(targetId, "row", true)}><LayoutGrid className="h-3 w-3" />Resp. tabulka</button>
        </div>
        {widgetTypesByCategory(scope, device).map((g) => (
          <div key={g.category} className="mb-1.5">
            <div className="text-[10px] uppercase text-muted-foreground">{g.label}</div>
            <div className="flex flex-wrap gap-1">
              {g.types.map((w) => (
                <PaletteItem key={w.id} w={w} onAdd={() => addWidget(targetId, w.id, w.defaultConfig ?? {}, w.defaultSize)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditorPanel;
